/* eslint-disable @typescript-eslint/no-unused-vars */
"use client"
import { ConfigProvider, Form, Input, message, Modal, Select, Button } from "antd";
import { Dispatch, SetStateAction, useEffect, useState, useCallback } from "react";
import { Province } from "@/core/models/address/province.model";
import { District } from "@/core/models/address/district.model";
import { Ward } from "@/core/models/address/ward.model";
import container from "@/lib/di/container";
import { AddressRepo } from "@/core/repos/address.repo";
import React from "react";
import { WaterRegistrationModel } from "@/core/models/water-registration/water-registration.model";
import { WaterRegistrationRepo } from "@/core/repos/tables/water-registration.repo";
import { getUseWaterRegisterStatusDisplay, UseWaterRegisterStatus } from "@/core/enums/global.enums";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { UnitTypeModel } from "@/core/models/unit-type/unit-type.model";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { FileAddOutlined, EditOutlined, PhoneOutlined } from "@ant-design/icons";
import { CreateContractModal } from "./CreateContractModal";
import { AppUserRepo } from "@/core/repos/tables/app-user.repo";
import { AppUserModel } from "@/core/models/app-user.model";

const addressRepo = container.get(AddressRepo);
const unitTypeRepo = container.get(UnitTypeRepo);
const waterRegistrationRepo = container.get(WaterRegistrationRepo);
const appUserRepo = container.get(AppUserRepo);

interface CreateModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterRegistrationModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<CreateModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<WaterRegistrationModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);
    const [unitTypes, setUnitTypes] = useState<UnitTypeModel[]>([]);
    const [isCreateContractModalOpen, setIsCreateContractModalOpen] = useState<boolean>(false);
    const [currentData, setCurrentData] = useState<WaterRegistrationModel>(data);
    const [creatorInfo, setCreatorInfo] = useState<AppUserModel | null>(null);
    const useWaterStatusOptions = [
        UseWaterRegisterStatus.New,
        UseWaterRegisterStatus.InProcess,
        UseWaterRegisterStatus.Completed,
        UseWaterRegisterStatus.Rejected,
    ]
    
    const canCreateContract = currentData.status === UseWaterRegisterStatus.Completed || 
                               currentData.status === UseWaterRegisterStatus.InProcess;
    
    const handleProvinceChange = useCallback(async (provinceId: string) => {
        const _districts = (await addressRepo.getAllDistricts(provinceId)).map(data => data[1]);
        setDistricts(_districts);
        setWards([]);
        form.setFieldValue("districtId", "");
        form.setFieldValue("wardId", "");
    }, [form])

    const handleDistrictChange = useCallback(async (districtId: string) => {
        const _wards = (await addressRepo.getAllWards(districtId)).map(data => data[1]);
        setWards(_wards);
        form.setFieldValue("wardId", "");
    }, [form])
    
    // Load creator information
    const loadCreatorInfo = useCallback(async (createdBy: string) => {
        if (createdBy && createdBy.length > 0) {
            try {
                const creator = await appUserRepo.detail(createdBy);
                if (creator) {
                    setCreatorInfo(creator);
                } else {
                    setCreatorInfo(null);
                }
            } catch (err) {
                console.log("Error loading creator info:", err);
                setCreatorInfo(null);
            }
        } else {
            setCreatorInfo(null);
        }
    }, []);

    //Attach hooks to handle data changes
    const handleDataChange = useCallback(async () => {
        setCurrentData(data);
        await handleProvinceChange(data.provinceId);
        await handleDistrictChange(data.districtId);
        form.setFieldsValue(data);
        if (data.id&&data.id.length>0) {
            setIsEdit(true);
            // Load creator info
            await loadCreatorInfo(data.createdBy);
        } else {
            setIsEdit(false);
            setCreatorInfo(null);
        }
    }, [data, form, handleProvinceChange, handleDistrictChange, loadCreatorInfo])
    
    useEffect(()=>{
        handleDataChange();
    }, [handleDataChange])

    //Initialize select options data
    const populateSelections = async () => {
        const _provinces = (await addressRepo.getAllProvinces()).map(data => data[1]);
        setProvinces(_provinces);
        const _unitTypes = (await unitTypeRepo.getAll());
        setUnitTypes(_unitTypes);
    }
    
    const init = useCallback(async () => {
        await populateSelections();
    }, [])
    
    //componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {
        init();
    }, [init]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                // If status changed, use updateStatus method (more efficient and follows API design)
                if (formData.status && formData.status !== data.status) {
                    const result = await waterRegistrationRepo.updateStatus(data.id, formData.status);
                    setIsModalOpen(false);
                    if (result) {
                        message.success({
                            content: "Đã cập nhật trạng thái"
                        });
                        setCurrentData({...data, status: formData.status});
                    } else {
                        message.error({
                            content: "Cập nhật trạng thái thất bại"
                        });
                    }
                } else {
                    // For other field updates (if any), use regular update
                    const result = await waterRegistrationRepo.update({...data, ...formData});
                    setIsModalOpen(false);
                    if (result) {
                        message.success({
                            content: "Đã cập nhật"
                        });
                        setCurrentData({...data, ...formData});
                    } else {
                        message.error({
                            content: "Cập nhật thất bại"
                        });
                    }
                }
            } else {
                const result = await waterRegistrationRepo.create({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    message.success({
                        content: "Thêm bản ghi mới thành công"
                    })
                } else {
                    message.error({
                        content: "Thêm bản ghi mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    
    const handleCreateContract = () => {
        setIsCreateContractModalOpen(true);
    };
    
    const handleContractCreated = () => {
        setIsCreateContractModalOpen(false);
        message.success({
            content: "Đã tạo hợp đồng thành công"
        });
    };
    
    const handleUpdateStatus = async () => {
        try {
            const currentStatus = form.getFieldValue("status");
            if (!currentStatus || currentStatus === currentData.status) {
                message.warning({
                    content: "Vui lòng chọn trạng thái mới"
                });
                return;
            }
            
            setLoading(true);
            const result = await waterRegistrationRepo.updateStatus(currentData.id, currentStatus);
            if (result) {
                setCurrentData({...currentData, status: currentStatus});
                message.success({
                    content: "Đã cập nhật trạng thái thành công"
                });
            } else {
                message.error({
                    content: "Cập nhật trạng thái thất bại"
                });
            }
        } catch (error) {
            console.log(error);
            message.error({
                content: "Có lỗi xảy ra khi cập nhật trạng thái"
            });
        }
        setLoading(false);
    };
  
    const handleCancel = () => {
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal open={isModalOpen} onCancel={handleCancel} width={970} style={{ top: 20 }}
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-between items-center px-1.5">
                        <div className="flex flex-row gap-2">
                            {isEdit && (
                                <Button 
                                    type="default" 
                                    icon={<EditOutlined />}
                                    onClick={handleUpdateStatus}
                                    loading={loading}
                                >
                                    Cập nhật trạng thái
                                </Button>
                            )}
                            <Button 
                                type="primary" 
                                icon={<FileAddOutlined />}
                                onClick={handleCreateContract}
                                disabled={!currentData.id || currentData.id.length === 0}
                            >
                                Tạo hợp đồng
                            </Button>
                        </div>
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT BẢN GHI</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<WaterRegistrationModel>
                        name="basic"
                        className="flex flex-col items-end"
                        autoComplete="off"
                        labelAlign="left"
                        layout="vertical"
                        form={form}
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        <div className="w-full">
                            <Form.Item<WaterRegistrationModel>
                                label="Tên"
                                name="name"
                            >
                                <Input disabled></Input>
                            </Form.Item>
                        </div>
                        <div className="flex flex-row">
                            <div className="flex flex-col items-end mr-[8px]">
                                <Form.Item<WaterRegistrationModel>
                                    label="Loại hình đơn vị"
                                    name="unitTypeId"
                                >
                                    <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled={isEdit}>
                                        {unitTypes.map(unitType => <Select.Option key={unitType.id} value={unitType.id}>{unitType.name}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Người đại diện"
                                    name="representative"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Chức vụ"
                                    name="title"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Địa chỉ văn phòng"
                                    name="officeAddress"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Điện thoại di động"
                                    name="mobilePhone"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Điện thoại cố định"
                                    name="landlinePhone"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Email"
                                    name="email"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Mã số thuế"
                                    name="taxCode"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Số tài khoản"
                                    name="bankNo"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Ngân hàng"
                                    name="bankName"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Lượng nước dự kiến sử dụng"
                                    name="estimateAmoutWater"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                            </div>
                            <div className="flex flex-col items-end">
                                <Form.Item<WaterRegistrationModel>
                                    label="Mục đích sử dụng"
                                    name="reason"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Tỉnh/ TP"
                                    name="provinceName"
                                >
                                    <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled={isEdit} onChange={handleProvinceChange}>
                                        {provinces.map(province => <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Quận/ Huyện"
                                    name="districtName"
                                >
                                    <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled={isEdit} onChange={handleDistrictChange}>
                                        {districts.map(district => <Select.Option key={district.id} value={district.id}>{district.name}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Xã/ Phường"
                                    name="wardName"
                                >
                                    <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled={isEdit}>
                                        {wards.map(ward => <Select.Option key={ward.id} value={ward.id}>{ward.name}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Địa chỉ đăng ký cấp nước"
                                    name="address"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="CCCD/CMND"
                                    name="identification"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Ngày cấp CCCD/CMND"
                                    name="identificationDate"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Trạng thái xử lý"
                                    name="status"
                                >
                                    <Select placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                        {useWaterStatusOptions.map(opt => <Select.Option key={opt} value={opt}>{getUseWaterRegisterStatusDisplay(opt)}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Ngày tạo"
                                    name="createdAt"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item
                                    label="Người tạo"
                                >
                                    {creatorInfo ? (
                                        <div className="flex flex-row items-center gap-2" style={{width: INPUT_WIDTH}}>
                                            <span>{creatorInfo.fullName || creatorInfo.userName}</span>
                                            {creatorInfo.phone && (
                                                <a 
                                                    href={`tel:${creatorInfo.phone}`}
                                                    className="flex items-center text-blue-600 hover:text-blue-800"
                                                    title={`Gọi ${creatorInfo.phone}`}
                                                >
                                                    <PhoneOutlined className="mr-1" />
                                                    {creatorInfo.phone}
                                                </a>
                                            )}
                                        </div>
                                    ) : (
                                        <Input 
                                            style={{width: INPUT_WIDTH}} 
                                            disabled 
                                            value={data.createdBy || ""}
                                            placeholder="Đang tải..."
                                        />
                                    )}
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Ngày cập nhật"
                                    name="updatedAt"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                                <Form.Item<WaterRegistrationModel>
                                    label="Cập nhật bởi"
                                    name="updatedBy"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                            </div>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
            <CreateContractModal
                isModalOpen={isCreateContractModalOpen}
                setIsModalOpen={setIsCreateContractModalOpen}
                registrationData={currentData}
                onSuccess={handleContractCreated}
            />
        </ConfigProvider>
    )
}